<template>
    <div class="ld-container" style="height: 90vh; width: 100%">
        <div class="row" style="height: 100%">
            <div class="list-area col-md-6">
                <div class="row">
                    <header-box :title="`${$t('시스템 관리')} > ${$t('알람 관리')} > ${$t('알람 기준 설정')}`" />
                </div>
                <div style="height: calc(100% - 100px)">
                    <table-list
                        ref="table-list"
                        :title="'알람 기준'"
                        :columns="columns"
                        :rows="alarmList"
                        :keyField="'_rowKey'"
                        :groupField="'alarmGroup'"
                        :toggleFields="['enabled']"
                        :useMaxHeight="true"
                        :transCodes="transCodes"
                        :controls="controls"
                        :mode="'edit'"
                        @button-click="handleListButtonClick"
                        @row-click="handleItemClick"
                        @toggle-changed="handleItemToggle"
                    />
                </div>
            </div>
            <div class="detail-area col-md-6">
                <alarm-detail
                    ref="AlarmDetail"
                    :title="'알람 기준'"
                    :alarmKey="alarmKey"
                    @data-updated="handleDetailUpdated"
                    @closed="handleDetailClosed"
                />
            </div>
        </div>
    </div>
</template>

<script>
import HeaderBox from "@views/component/headerBox/analysis/Header.vue";
import TableList from "@views/component/v2.1/ListDetailView/TableList.vue";
import AlarmDetail from "./components/AlarmDetail.vue";
import backEndApi from "@src/api/backEndApi";
import * as popupMessages from "@src/consts/popupMessageConsts";
import { formatChangeCriteria, formatD0Criteria } from "./components/panels/ruleParamsUi.js";

export default {
    components: {
        HeaderBox,
        TableList,
        AlarmDetail,
    },
    data() {
        return {
            alarmKey: null,
            alarmList: [],
            popupMessages,
            controls: [
                { event: "delete", label: "삭제", class: "btn-danger" },
                { event: "refresh", label: "갱신", class: "bg-white text-dark" },
                {
                    event: "status",
                    label: "사용",
                    class: "bg-white text-dark",
                    options: { enabled: "Y" },
                },
                {
                    event: "status",
                    label: "미사용",
                    class: "bg-white text-dark",
                    options: { enabled: "N" },
                },
                { event: "regist", label: "추가", class: "btn-primary" },
            ],
            transCodes: [
                {
                    field: "enabled",
                    codes: this.$store.state.commonCodes.enabled,
                },
                {
                    field: "alarmTypeLabel",
                    codes: [
                        { value: "Low / High", text: "Low / High" },
                        { value: "Low low / High high", text: "Low low / High high" },
                        { value: "True / False", text: "True / False" },
                        { value: "상태 변경", text: "상태 변경" },
                    ],
                },
                {
                    field: "promoteAlarmTypeLabel",
                    codes: [
                        { value: "이벤트", text: "이벤트" },
                        { value: "설비 오류", text: "설비 오류" },
                    ],
                },
            ],
            columns: [
                {
                    label: this.$t("알람 그룹"),
                    field: "alarmGroup",
                    type: "text",
                    hidden: true, // 그룹핑용 — 표 컬럼에는 미표시
                },
                {
                    label: this.$t("알람 유형"),
                    field: "promoteAlarmTypeLabel",
                    type: "text",
                    width: "80px",
                },
                {
                    label: this.$t("관제점 주소"),
                    field: "rowTitle",
                    type: "text",
                    compositionFields: ["ptAddr", "alarmTypeLabel"],
                },
                {
                    label: this.$t("알람 발생 기준"),
                    field: "criteriaHtml",
                    type: "text",
                    width: "240px",
                    sortable: false,
                    tdClass: "criteria-merged-td",
                    formatter: (val) => val || "",
                },
                {
                    label: this.$t("알람메세지"),
                    field: "description",
                    type: "text",
                    tdClass: "left-align-wrap",
                    sortable: false,
                },
                {
                    label: this.$t("사용여부"),
                    field: "enabled",
                    width: "80px",
                    type: "text",
                },
            ],
        };
    },
    async mounted() {
        await this.getDataList();
    },
    methods: {
        fmtCriteria(v) {
            if (v === null || v === undefined || v === "") return "—";
            const n = Number(v);
            return Number.isFinite(n) ? String(n) : String(v);
        },
        buildCriteriaHtml(v) {
            const isA0 = v.alarmType === "A0";
            const isA1 = v.alarmType === "A1";
            const isD0 = v.alarmType === "D0";
            const isD1 = v.alarmType === "D1";

            // 마지막 칸은 디지털 판정 공용이다. D0는 T/F, D1은 감지 방향을 적는다.
            const digital = isD0
                ? formatD0Criteria(v.hh, v.ruleParams)
                : isD1
                  ? formatChangeCriteria(v.ruleParams)
                  : "—";

            const cells = [
                { val: isA1 ? this.fmtCriteria(v.ll) : "—", on: isA1 },
                { val: isA0 || isA1 ? this.fmtCriteria(v.l) : "—", on: isA0 || isA1 },
                { val: isA0 || isA1 ? this.fmtCriteria(v.h) : "—", on: isA0 || isA1 },
                { val: isA1 ? this.fmtCriteria(v.hh) : "—", on: isA1 },
                { val: digital, on: isD0 || isD1 },
            ];

            const vals = cells
                .map((c) => `<span class="ac-v${c.on ? " is-on" : ""}">${c.val}</span>`)
                .join("");

            return `<div class="ac-grid"><div class="ac-row ac-body">${vals}</div></div>`;
        },
        mapRow(v) {
            const alarmTypeLabel =
                v.alarmType === "A0"
                    ? "Low / High"
                    : v.alarmType === "A1"
                      ? "Low low / High high"
                      : v.alarmType === "D0"
                        ? "True / False"
                        : v.alarmType === "D1"
                          ? "상태 변경"
                          : v.alarmType || "-";

            const promote = v.promoteAlarmType || "Event";
            const promoteFromCode = (this.$store.state.commonCodes?.alarmType || []).find((c) => c.value === promote);
            const promoteAlarmTypeLabel = promoteFromCode
                ? promoteFromCode.text
                : promote === "EquipFault"
                  ? "설비 오류"
                  : "이벤트";

            return {
                ...v,
                _rowKey: `${v.alarmGroup}||${v.ptAddr}`,
                alarmTypeLabel,
                promoteAlarmTypeLabel,
                criteriaHtml: this.buildCriteriaHtml(v),
                enabled: v.isUse ? "Y" : "N",
            };
        },
        parseAlarmKey(key) {
            if (!key || typeof key !== "string") return null;
            const idx = key.indexOf("||");
            if (idx < 0) return null;
            return {
                alarmGroup: key.slice(0, idx),
                ptAddr: key.slice(idx + 2),
            };
        },
        async handleListButtonClick(event, options) {
            switch (event) {
                case "regist":
                    await this.registNew();
                    break;
                case "delete":
                    await this.deleteChecked();
                    break;
                case "refresh":
                    await this.getDataList(event);
                    break;
                case "status":
                    await this.changeStatusChecked(options);
                    break;
                default:
                    console.warn("Unhandled Component Event. event:", event, "options:", options);
                    break;
            }
        },
        async handleItemToggle(key, field, status) {
            await this.changeStatus(key, field, status);
        },
        async handleItemClick(row, idx, toggle, check) {
            if (toggle || check) return;

            const confirm = await this.$refs["AlarmDetail"].notifyCancelEdit();
            if (confirm) {
                this.alarmKey = row._rowKey;
            }
        },
        async getDataList(event) {
            try {
                const result = await backEndApi.alarm.searchAlarm();
                if (result.status == 200 && !this.$err(result.data)) {
                    this.alarmList = (Array.isArray(result.data) ? result.data : []).map((v) => this.mapRow(v));
                    if (event) await this.alertNoti(popupMessages.COMMON_REFRESH_POPUP_MESSAGE);
                } else if (result.status != 200) {
                    this.alertWarning(
                        `${result.data.message}`,
                        "",
                        `${typeof result.data.detail == "string" ? result.data.detail : ""}`
                    );
                }
                return result;
            } catch (e) {
                console.error(e);
            }
        },
        async deleteChecked() {
            try {
                const confirm = await this.alertConfirmWarning(popupMessages.COMMON_DELETE_CONFIRM_POPUP_MESSAGE);
                if (!confirm.value) return;

                const checked = this.$refs["table-list"].getCheckedRows();
                if (this.isEmpty(checked)) {
                    this.alertNoti(popupMessages.COMMON_DELETE_NOTI_POPUP_MESSAGE);
                    return;
                }

                for (const item of checked) {
                    const { data } = await backEndApi.alarm.deleteAlarm(item.alarmGroup, item.ptAddr);
                    if (this.$err(data)) return;
                }

                await this.alertNoti(popupMessages.COMMON_DELETE_POPUP_MESSAGE);
                const cleared = await this.getDataList();
                if (cleared) await this.$refs["AlarmDetail"].clearData();
                this.alarmKey = null;
            } catch (e) {
                this.alertNoti(popupMessages.COMMON_REMOVE_ERROR_POPUP_MESSAGE);
                console.error(e);
            }
        },
        async changeStatusChecked(options) {
            try {
                const checked = this.$refs["table-list"].getCheckedRows();
                if (this.isEmpty(checked)) {
                    this.alertNoti(popupMessages.COMMON_STATUS_CHANGE_NOTI_POPUP_MESSAGE);
                    return;
                }

                for (const item of checked) {
                    if (options && options.hasOwnProperty("enabled")) {
                        await this.changeStatus(item._rowKey, "enabled", options.enabled, false);
                    }
                }
                await this.alertNoti(popupMessages.COMMON_STATUS_CHANGE_SUCCESS_POPUP_MESSAGE);
                await this.getDataList();
            } catch (e) {
                console.error(e);
            }
        },
        async changeStatus(key, field, status, showNoti = true) {
            const parsed = this.parseAlarmKey(key);
            if (!parsed) return;

            try {
                if (field === "enabled") {
                    const result = await backEndApi.alarm.getAlarm(parsed.alarmGroup, parsed.ptAddr);
                    if (this.$err(result.data)) return;

                    const alarm = result.data;
                    alarm.isUse = status === "Y";

                    const updateResult = await backEndApi.alarm.updateAlarm(alarm);
                    if (this.$err(updateResult.data)) return;
                }

                if (showNoti) {
                    await this.alertNoti(popupMessages.COMMON_STATUS_CHANGE_SUCCESS_POPUP_MESSAGE);
                    await this.getDataList();
                }
            } catch (e) {
                console.error(e);
            }
        },
        async registNew() {
            const confirm = await this.$refs["AlarmDetail"].notifyCancelEdit();
            if (!confirm) return;
            this.alarmKey = null;
            this.$refs["AlarmDetail"].newData();
        },
        async handleDetailUpdated(action, detail) {
            await this.getDataList();
            if (detail && detail.alarmGroup && detail.ptAddr) {
                this.alarmKey = `${detail.alarmGroup}||${detail.ptAddr}`;
            }
        },
        handleDetailClosed() {
            this.alarmKey = null;
        },
    },
};
</script>

<style scoped>
.ld-container {
    height: 90vh;
    width: 100%;
    background-color: white;
    border-radius: 10px;
    overflow: hidden;

    padding: 10px 30px 10px 20px;
}

.ld-container .list-area {
    height: 100%;
    overflow: hidden;
    padding: 10px;
}

.ld-container .detail-area {
    height: 100%;
    margin-right: 00px;
    padding: 0px 10px 0px 10px;
    border-top: 1px solid #f4f4f4;
    border-right: 2px solid #e7e7e7;
    border-bottom: 2px solid #e7e7e7;
    border-left: 1px solid #f4f4f4;
    background-color: #f9f9f9;
    border-radius: 10px;
}

.ld-container >>> .criteria-merged-td {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
}

.ld-container >>> .ac-grid {
    min-width: 210px;
}

.ld-container >>> .ac-row {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.ld-container >>> .ac-v {
    font-size: 12px;
    color: #ccc;
    line-height: 1.3;
}

.ld-container >>> .ac-v.is-on {
    color: #222;
    font-weight: 600;
}
</style>
